/**
 * 콘텐츠 좌우 여백에 떠 있는 세로배너(160x600).
 *
 * `topPx` 로 상단 고정 위치를 지정한다(각 페이지의 히어로 높이에 맞춰 히어로 아래로).
 * 스크롤을 내려 푸터가 화면에 들어오면 배너를 위로 밀어 올려(top 감소) 배너 하단이
 * 항상 푸터 위에 머물게 한다 → 푸터가 배너를 가리거나 배너가 푸터를 침범하지 않음.
 *
 * 콘텐츠가 중앙 정렬(max-w-*)이고 좌우 여백이 있는 페이지 전용.
 * 지도 분할형(시설검색)처럼 전체화면 오버레이 레이아웃에는 쓰지 않는다.
 */
import { useEffect, useRef, useState } from 'react'
import PromotionSlot from '@/components/common/PromotionSlot'

interface StickySideBannerProps {
  slotKey: string
  side: 'left' | 'right'
  /** 상단 고정 위치(px). 각 페이지 히어로 하단 아래로 맞춘다. */
  topPx: number
  /**
   * 화면 중앙에서 배너 바깥 모서리까지의 거리(px).
   * = 콘텐츠 반폭 + 배너 160 + 본문과의 여백
   *
   * 여백은 48px 을 기준으로 한다(16px 은 배너가 본문에 붙어 산만하다는 지적, 2026-08-25).
   *   max-w-2xl(672) → 336+160+48 = 544  체크리스트·계산기
   *   max-w-3xl(768) → 384+160+48 = 592  커뮤니티
   *   max-w-5xl(1024)→ 512+160+48 = 720  계산기 문답·결과
   *
   * ⚠️ max-w-6xl(1152) 은 48px 을 줄 수 없다. 2xl(1536) 기준 한쪽 여유가 192px 뿐이라
   *    배너 160 을 빼면 32px 만 남는다. 더 벌리면 배너가 화면 밖으로 밀린다.
   *    그래서 기본값 752(여백 16)를 유지한다 — 홈·시설상세·비용계산기가 해당한다.
   */
  edgeOffsetPx?: number
  /** 노출 시작 브레이크포인트 (기본 2xl). 콘텐츠가 좁으면 'xl'. */
  breakpoint?: 'xl' | '2xl'
}

/** 배너 하단과 푸터 사이 최소 간격(px). */
const FOOTER_GAP = 16

/**
 * 배너가 올라갈 수 있는 최상단(px). 헤더(약 64px) 아래로 둔다.
 * 여기보다 더 올려야 푸터를 피할 수 있는 상황이면 배너를 넣지 않는다 —
 * 헤더에 가려진 배너는 노출도 안 되고 보기만 나쁘다.
 */
const MIN_TOP = 72

export default function StickySideBanner({
  slotKey,
  side,
  topPx,
  edgeOffsetPx = 752,
  breakpoint = '2xl',
}: StickySideBannerProps) {
  const bannerRef = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState(topPx)
  /** 푸터를 피할 자리가 없으면 그리지 않는다. */
  const [hasRoom, setHasRoom] = useState(true)

  useEffect(() => {
    let raf = 0
    const recalc = () => {
      const el = bannerRef.current
      if (!el) return
      const height = el.offsetHeight
      // 광고가 아직 안 뜬 상태(높이 0)면 판단을 미룬다. 이때 계산하면 클램프가
      // 걸리지 않아, 나중에 광고가 600px 로 자라며 푸터를 덮는다.
      if (height === 0) return

      const footer = document.querySelector('footer')
      const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight
      // 푸터에 닿으면 배너 하단이 푸터 위에 머물도록 top 을 위로 클램프.
      const maxTop = footerTop - FOOTER_GAP - height

      if (maxTop < MIN_TOP) {
        setHasRoom(false)
        return
      }
      setHasRoom(true)
      setTop(topPx > maxTop ? maxTop : topPx)
    }
    const schedule = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(recalc)
    }

    recalc()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)

    // 광고는 비동기로 뜬다. 뜨는 순간 높이가 0 → 600 으로 바뀌므로 그때 다시 계산해야 한다.
    // 본문이 짧아 스크롤이 없는 페이지에서는 이 관찰이 유일한 재계산 계기다.
    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule)
      if (bannerRef.current) observer.observe(bannerRef.current)
      // 본문이 늦게 채워지면 푸터 위치도 바뀐다.
      if (document.body) observer.observe(document.body)
    }

    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      observer?.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [topPx])

  // 브레이크포인트 클래스는 정적 리터럴이어야 Tailwind JIT 가 인식한다.
  const visibility = breakpoint === 'xl' ? 'hidden xl:block' : 'hidden 2xl:block'

  return (
    <div
      // 자리가 없으면 감춘다. 언마운트하면 높이가 0 이 되어 다시 자리가 있다고
      // 판단하는 진동이 생기므로, 마운트는 유지하고 보이기만 끈다.
      className={`${visibility} fixed z-30 ${hasRoom ? '' : 'invisible'}`}
      style={{ top, [side]: `calc(50% - ${edgeOffsetPx}px)` }}
      aria-hidden={!hasRoom}
    >
      <div ref={bannerRef}>
        <PromotionSlot slotKey={slotKey} />
      </div>
    </div>
  )
}
